import React, { useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { extractQuoteFields, QUOTE_FIELD_KEYS } from '../utils';

const STORAGE_BUCKET = 'item-docs';

const FIELD_LABELS = {
  price:      'Price',
  leadTime:   'Lead Time',
  product:    'Product',
  sku:        'SKU',
  colorway:   'Colorway',
  finish:     'Finish',
  dimensions: 'Dimensions',
  qty:        'Qty',
  vendor:     'Vendor',
  notes:      'Notes',
};

export default function QuoteSummaryPanel({ item, docs, onDocUploaded }) {
  const [summary, setSummary] = useState(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const existingQuote = (docs || []).find(
    d => d.doc_type === 'Quote' || d.type === 'Quote'
  );

  async function processFile(file) {
    setLoading(true);
    setError('');
    setPendingFile(null);
    try {
      // Upload file to Supabase storage
      const path = `${item.id}/${Date.now()}-${file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(path, file, { upsert: true });
      if (uploadErr) throw new Error(uploadErr.message);

      const { data: urlData } = supabase.storage
        .from(STORAGE_BUCKET)
        .getPublicUrl(path);
      const publicUrl = urlData.publicUrl;

      // Ask Claude to extract quote fields
      const resp = await fetch('/api/claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: publicUrl,
          filename: file.name,
          prompt: 'Extract the key fields from this vendor quote as JSON with keys: price, leadTime, product, sku, colorway, finish, dimensions, qty, vendor, notes. Return only valid JSON.',
        }),
      });

      if (!resp.ok) throw new Error('API error ' + resp.status);

      const data = await resp.json();
      const fields = extractQuoteFields(JSON.parse(data.text));
      setSummary(fields);

      if (onDocUploaded) {
        onDocUploaded({ path, url: publicUrl, name: file.name, doc_type: 'Quote' });
      }
    } catch (err) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (existingQuote) {
      setPendingFile(file);
    } else {
      processFile(file);
    }
  }

  // Replace / add-version prompt
  if (pendingFile && existingQuote) {
    return (
      <div style={{ padding: 16 }}>
        <p style={{ marginBottom: 12, fontSize: 13 }}>
          A quote is already on file (<strong>{existingQuote.name}</strong>).
          What would you like to do with <strong>{pendingFile.name}</strong>?
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => processFile(pendingFile)}>Replace existing</button>
          <button onClick={() => processFile(pendingFile)}>Add as new version</button>
          <button onClick={() => setPendingFile(null)}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      {/* Error */}
      {error && (
        <div style={{ color: '#c00', marginBottom: 12, fontSize: 13 }}>
          Could not read quote — {error}
        </div>
      )}

      {/* Summary table */}
      {summary && (
        <div style={{ marginBottom: 16 }}>
          {QUOTE_FIELD_KEYS.filter(k => summary[k]).map(k => (
            <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 13 }}>
              <span style={{ color: '#888', minWidth: 90 }}>{FIELD_LABELS[k] || k}</span>
              <span>{summary[k]}</span>
            </div>
          ))}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>
          Processing quote…
        </div>
      )}

      {/* Existing quote row */}
      {existingQuote && !loading && (
        <div style={{ marginBottom: 12, fontSize: 13 }}>
          <span style={{ color: '#555' }}>{existingQuote.name}</span>
          <button
            style={{ marginLeft: 12 }}
            onClick={() => {
              fetch('/api/claude', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: existingQuote.url, filename: existingQuote.name, prompt: 'Extract quote fields as JSON.' }),
              })
                .then(r => r.json())
                .then(data => setSummary(extractQuoteFields(JSON.parse(data.text))))
                .catch(err => setError(err.message || 'Unknown error'));
            }}
          >
            Re-run summary
          </button>
        </div>
      )}

      {/* Upload prompt (no existing quote, no summary yet) */}
      {!existingQuote && !loading && !summary && (
        <div style={{ color: '#888', fontSize: 13, marginBottom: 8 }}>
          Upload quote PDF to extract pricing details.
        </div>
      )}

      {/* File input */}
      <input
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleFileChange}
        style={{ marginTop: 4, fontSize: 12 }}
      />
    </div>
  );
}
